import { useVideoInfo } from "@/api/Video";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { JitsiVideo } from "@/components/Video/JitsiVideo";
import styles from "./VideoMeeting.module.scss";
import { useSearchParams } from "react-router";
import type { IVideoService } from "@medoma/models/video";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useLingui } from "@lingui/react/macro";

export const VideoMeeting = () => {
  const [searchParams] = useSearchParams();
  const requestedVideoService = searchParams.get("video-service");
  const videoService: IVideoService =
    requestedVideoService === "jitsi" ? "Jitsi" : "Jitsi";
  const {
    data: videoInfo,
    isError,
    isPending,
    error,
  } = useVideoInfo(videoService);
  const { t } = useLingui();

  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("video:started");
    }
  }, []);

  if (isPending) {
    return <Loading message={t`Startar videosamtal`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={t`Det blev ett fel med videosamtalet.`} />;
  }

  if (!videoInfo) {
    return <ErrorMessage message={t`Det blev ett fel med videotjänsten.`} />;
  }

  return (
    <div className={styles.container}>
      {videoInfo.videoService === "Jitsi" ? (
        <JitsiVideo
          roomName={videoInfo.room}
          // Guide: https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-configuration/
          // All config options: https://github.com/jitsi/jitsi-meet/blob/master/config.js
          // All config options available to be overwritten from client: https://github.com/jitsi/jitsi-meet/blob/master/react/features/base/config/configWhitelist.ts
          configOverwrite={{
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            toolbarButtons: ["hangup"],
            customParticipantMenuButtons: [], // Could be used to add some custom functionality
            welcomePage: {
              disabled: true,
            },
            prejoinConfig: {
              enabled: false,
            },
            disableDeepLinking: true, // Will not ask user to open in app (defaults to browser)
            subject: t`Videosamtal med din vårdgivare`,
            notifications: [{ "notify.grantedTo": false }], // Avoid mentioning moderator rights are given
            hideDisplayName: true, // Don't show the name of who you are talking to
            conferenceInfo: {
              autoHide: [], // Hides all things from the top menu (quality, time, name of meeting)
            },
            filmstrip: {
              disableResizable: true, // Can't disable whole filmstrip it seems, but at least we can simplify by not making it resizable
            },
            disableModeratorIndicator: true, // Avoid showing "M"
            disableLocalVideoFlip: true, // Avoid strange actions
            disableSelfViewSettings: true, // Avoid strange actions
            enableNoisyMicDetection: false, // Avoid unwanted notifications (care personnel can tell the patient instead)
            disableSelfView: false, // Self view disappeared for a while without this config, keep just in case
          }}
          jwt={videoInfo.token}
          videoServer={videoInfo.domain}
        />
      ) : null}
    </div>
  );
};
